<template>
  <div>
    <div id="mobile-topbar" class="d-print-none">
      <span>Logged in as {{ user.userId }}</span>
      <a href="javascript:document.getElementById('logoutForm').submit()" class="log-off-link">Log off</a>
      <div class="toggle-menu-icon" @click="changeMobileNavbarState(!mobileNavbar.expanded)">
        <font-awesome-icon icon="bars" class="toggle-menu-item" />
      </div>
    </div>
    <div :class="{ 'nav-modal': 'true', show: mobileNavbar.expanded }" @click="changeMobileNavbarState(false)"></div>
    <div id="menu" :class="{
      'd-print-none': 'true',
      'ui-resizable': 'true',
      show: mobileNavbar.expanded,
    }">
      <div class="slimScrollDiv">
        <div class="slim-scroll">
          <!-- Sidebar Profile -->
          <div class="profile" v-if="user.userId !== undefined">
            <a class="img" :href="'https://webapp4.asu.edu/directory/photo?user=' + user.userId
              ">
              <img :src="'https://webapp4.asu.edu/directory/photo?user=' + user.userId
                " />
            </a>
            <span>
              <strong>Welcome</strong>
              <a href="/">{{ user.firstName }}</a>
            </span>
          </div>
          <!-- // Sidebar Profile END -->
          <menu-tree :items="navItems"></menu-tree>
          <div class="clearfix"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { routes } from "../routes";
import MenuTree from "@/components/menu/menu-tree";
import store from "../store";
import { mapActions, mapState } from "vuex";
import * as _ from "lodash"; // eslint-disable-line no-unused-vars
import roleChecks from "../misc/role-checks";

export default {
  mixins: [roleChecks],
  data() {
    return {
      routes,
      showMobileNavbar: false,
      navItems: []
    };
  },

  components: {
    "menu-tree": MenuTree
  },

  computed: {
    ...mapState({
      user: state => state.auth.user,
      mobileNavbar: state => state.navbar.mobileNavbar
    }),

    accessibleRoutes: function () {
      var accessible = [];
      _.forEach(this.routes, function (route) {
        // Only display a route if the user has access to it
        if (_.has(route.meta, "roles")) {
          var authorizedRoles = route.meta.roles;
          if (store.getters.isAuthorized(authorizedRoles)) {
            accessible.push(route);
          }
        } else {
          accessible.push(route);
        }
      });
      return accessible;
    }
  },

  watch: {
    accessibleRoutes: function () {
      // pass in null to tell it to start removing items from tree with depth 0, not a sublist of children
      this.setNavItems();
    }
  },

  methods: {
    setNavItems: function () {
      this.navItems = [];

      // set the nav menu depending on which roles user has
      // TODO: set this more dynamically based on user roles from the store
      this.navItems.push({ text: "Home", icon: "home", route: "/" });

      if (this.isWorkloadsSubmitter) {
        this.navItems.push({
          text: "My Dashboard",
          icon: "user",
          route: "/dashboard"
        });
      }

      var workloadsBranch = {
        text: "Workloads",
        icon: "chart-pie",
        children: []
      };

      if (this.isWorkloadsSubmitter) {
        workloadsBranch.children.push({
          text: "Add Workload",
          route: { name: "myWorkloads" }
        });
      }

      if (this.isWorkloadsManager || this.isWorkloadsViewer) {
        workloadsBranch.children.push({
          text: "All Workloads",
          route: { name: "facultyWorkloads" }
        });

        workloadsBranch.children.push({
          text: "Approved Workloads",
          route: { name: "workloads" }
        });

        workloadsBranch.children.push({
          text: "Pending Workloads",
          route: { name: "pendingWorkloads" }
        });

        workloadsBranch.children.push({
          text: "Denied Workloads",
          route: { name: "deniedWorkloads" }
        });

        workloadsBranch.children.push({
          text: "Unsubmitted Faculty",
          route: { name: "facultyUnsubmitted" }
        });
      }

      if (workloadsBranch.children.length > 0) {
        this.navItems.push(workloadsBranch);
      }

      // until otherwise requested, this menu will be hidden on prod
      if (process.env.NODE_ENV !== "production") {
        if (this.isSPViewer || this.isSPSubmitter || this.isSPManager) {
          var spaBranch = {
            text: "Summer Pay",
            icon: "money-bill-wave",
            children: []
          };

          spaBranch.children.push({
            text: "Summer Pay List",
            route: { name: "SummerPayRequests" }
          });

          if (this.isSPSubmitter || this.isSPManager) {
            spaBranch.children.push({
              text: "Add Request",
              route: { name: "summerPayAdd" }
            });
          }

          this.navItems.push(spaBranch);
        }
      }

      if (this.isBuyoutManager || this.isBuyoutViewer) {
        this.navItems.push({
          text: "Buyout Requests",
          icon: "comments-dollar",
          route: { name: "buyoutRequests" }
        });
      }

      if (this.isOverloadsManager) {
        this.navItems.push({
          text: "Overload Requests",
          icon: "calendar-plus",
          route: { name: "overloadRequests" }
        });
      }

      if (this.isAdminViewer || this.isAdminSupervisor) {
        this.navItems.push({
          text: "Administrative Requests",
          icon: "list",
          route: { name: "adminRequests" }
        });
      }

      if (this.isCourseEquivalentsManager || this.isCourseEquivalentsViewer) {
        this.navItems.push({
          text: "Course Equivalents",
          icon: "book-open",
          route: { name: "courseEquivalents" }
        });
      }

      if (this.isWorkloadsManager || this.isWorkloadsViewer) {
        var reports = {
          text: "Reports",
          icon: "scroll",
          route: { name: "reports" }
        };
        this.navItems.push(reports);
      }

      if (this.isExpertsAdmin || this.isExpertsEditor || this.isExpertsViewer) {
        this.navItems.push({
          text: "Community Partnerships",
          icon: "external-link-alt",
          link:
            process.env.NODE_ENV !== "production"
              ? "https://communitypartnerships-dev.chs.asu.edu"
              : "https://communitypartnerships.chs.asu.edu"
        });
      }

      if (this.isTranslationalTeam) {
        this.navItems.push({
          text: "Translational Teams",
          icon: "external-link-alt",
          link:
            process.env.NODE_ENV !== "production"
              ? "https://translationalteams-dev.chs.asu.edu"
              : "https://translationalteams.chs.asu.edu"
        });
      }

      this.navItems.push({
        text: "Proposal Intake",
        icon: "external-link-alt",
        link:
          process.env.NODE_ENV !== "production"
            ? "https://grantmanagement-dev.health.asu.edu"
            : "https://grants.health.asu.edu"
      });

      this.navItems.push({
        text: "Request Help",
        icon: "question-circle",
        link: "https://links.asu.edu/chsapplicationintake"
      });
    },

    ...mapActions(["expandNavbar", "contractNavbar"]),

    changeMobileNavbarState(expand) {
      if (expand) {
        this.$store.dispatch("expandNavbar");
      } else {
        this.$store.dispatch("contractNavbar");
      }
    }
  }
};
</script>

<style>
#mobile-topbar {
  display: none;
}

.nav-modal {
  display: none;
}

#menu {
  width: 235px;
  position: static;
  float: left;
  left: 0;
  top: 0;
  overflow: hidden;
  margin-bottom: -99999px;
  padding-bottom: 99999px;
  background: #44413e;
  color: white;
}

#menu .profile span {
  text-align: left;
  display: block;
  padding-top: 3px;
  width: 130px;
  float: right;
}

#menu .profile span strong {
  display: block;
  color: white;
  font-size: 14px;
  font-weight: normal;
}

#menu .profile span a {
  color: #e04545;
  font-size: 14px;
  padding-right: 25px;
  text-decoration: none;
}

#menu .profile span a:hover {
  text-decoration: underline;
}

.profile {
  padding: 10px;
  border-bottom: 1px solid #575452;
  overflow: auto;
}

.img {
  border: 2px solid #696765;
  width: 40px;
  border-radius: 5px;
  float: left;
}

.img:hover {
  border-color: #9c9a98;
}

img {
  max-width: 100%;
  height: auto;
  border-radius: 5px;
}

.slimScrollDiv {
  position: relative;
  overflow: hidden;
  width: auto;
}

.slim-scroll>ul {
  list-style: none;
  float: left;
  margin: 0;
  padding: 0;
  display: block;
  width: 100%;
  background: #52504d;
  border-top: 1px solid #64625f;
}

.slim-scroll>ul>li {
  display: block;
  height: 39px;
  max-height: 39px;
  line-height: 39px;
  border-bottom: 1px solid #64625f;
  position: relative;
  overflow: hidden;
  padding: 0;
  margin: 0;
  transition: max-height 2s;
}

.slim-scroll>ul>li.expanded {
  height: auto;
  max-height: 900px;
  background: #42403e;
  transition: max-height 2s;
}

.slim-scroll>ul ul {
  list-style: none;
  padding: 0;
}

.slim-scroll>ul ul li a {
  font-weight: 400;
}

.slim-scroll>ul ul li {
  height: auto;
}

.slim-scroll>ul>li a,
.submenu-name {
  display: block;
  width: auto;
  margin: 0;
  color: white;
  height: 39px;
  font-size: 13px;
  font-weight: 600;
  padding-left: 45px;
  text-decoration: none;
}

.slim-scroll>ul>li a:hover,
.submenu-name:hover {
  background: #363432;
  cursor: pointer;
}

.expanded .submenu-name {
  background: #42403e;
}

.children-count {
  display: inline-block;
  background: #363432;
  color: white;
  font-weight: 600;
  font-size: 10px;
  padding: 1px 8px;
  position: absolute;
  border-radius: 3px;
  top: 10px;
  right: 15px;
  line-height: normal;
}

.nav-icon {
  left: 15px;
  top: 10px;
  position: absolute;
  font-size: 17px;
}

.slim-scroll {
  overflow: hidden;
  width: auto;
}

.log-off-link {
  color: #e04545;
  margin-left: 5px;
}

@media only screen and (min-device-width: 320px) and (max-device-width: 480px) {
  #mobile-topbar {
    display: block;
    width: 100%;
    background: #44413e;
    position: absolute;
    height: 30px;
    top: 0;
    color: white;
    padding: 3px;
  }

  .toggle-menu-icon {
    float: right;
    margin-right: 5px;
  }

  #menu {
    left: -235px;
    position: absolute;
    transition: 1s;
    z-index: 100;
  }

  #menu.show {
    left: 0;
    transition: 1s;
  }

  .nav-modal.show {
    display: block;
    width: 100vw;
    height: 100vh;
    z-index: 4;
    background-color: rgba(0, 0, 0, 0.4);
    transition: 1s;
  }
}
</style>
